import React, { useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { updatePageSEO, generateStructuredData } from '../../utils/seo';

const AboutPage: React.FC = () => {
  useEffect(() => {
    const siteUrl = window.location.origin;
    updatePageSEO({
      title: '会社概要｜おかやま司法書士事務所 - 岡山市・倉敷市の司法書士',
      description: 'おかやま司法書士事務所の会社概要。2000年設立、岡山市北区に所在。代表司法書士岡山太郎をはじめ5名のスタッフが岡山県内で司法書士業務を提供。岡山県司法書士会所属、簡裁訴訟代理等関係業務認定。',
      keywords: '会社概要,おかやま司法書士事務所,岡山太郎,司法書士,岡山市,倉敷市,事務所情報',
      canonical: `${siteUrl}/about`,
      ogTitle: '会社概要｜おかやま司法書士事務所',
      ogDescription: 'おかやま司法書士事務所の会社概要。2000年設立、岡山市北区に所在。代表司法書士岡山太郎をはじめ5名のスタッフが岡山県内で司法書士業務を提供。',
      ogUrl: `${siteUrl}/about`,
      structuredData: generateStructuredData.about()
    });
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative py-16 md:py-20 bg-gradient-to-r from-blue-600 to-blue-800">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/5a231c209cb843df321343a189f3176b.jpg"
              alt="事務所外観"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-blue-900/70"></div>
          </div>
          <div className="relative z-10 container mx-auto px-4 text-center">
            <div className="flex justify-center mb-4 md:mb-6 overflow-x-auto">
              <div className="flex space-x-2 px-4">
                <a href="/" className="px-3 md:px-4 py-2 bg-white/20 text-white rounded-full hover:bg-white/30 transition-colors whitespace-nowrap text-xs md:text-sm">
                  ホーム
                </a>
                <a href="/about" className="px-3 md:px-4 py-2 bg-white text-blue-600 rounded-full whitespace-nowrap text-xs md:text-sm">
                  会社概要
                </a>
                <a href="/recruit" className="px-3 md:px-4 py-2 bg-white/20 text-white rounded-full hover:bg-white/30 transition-colors whitespace-nowrap text-xs md:text-sm">
                  求人情報
                </a>
                <a href="/blog" className="px-3 md:px-4 py-2 bg-white/20 text-white rounded-full hover:bg-white/30 transition-colors whitespace-nowrap text-xs md:text-sm">
                  ブログ
                </a>
              </div>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">会社概要</h1>
            <p className="text-base md:text-xl text-gray-200 max-w-3xl mx-auto px-4">
              2000年の設立以来、岡山県内で地域密着の司法書士サービスを提供し続けています
            </p>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 md:mb-6">おかやま司法書士事務所について</h2>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4 md:mb-6">
                  おかやま司法書士事務所は、2000年の設立以来、岡山市・倉敷市を中心とした岡山県内で、
                  地域の皆様に信頼される司法書士サービスを提供してまいりました。
                </p>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-4 md:mb-6">
                  不動産登記、商業登記、相続手続き、債務整理、成年後見など、
                  幅広い司法書士業務を通じて、お客様の大切な財産と権利をお守りしています。
                </p>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  経験豊富なスタッフが、お客様一人ひとりに寄り添い、
                  最適な解決策をご提案いたします。法的手続きでお困りの際は、
                  お気軽にご相談ください。
                </p>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20law%20office%20interior%2C%20modern%20consultation%20room%20with%20comfortable%20seating%2C%20natural%20lighting%2C%20wooden%20furniture%2C%20legal%20books%20on%20shelves%2C%20warm%20and%20welcoming%20atmosphere%2C%20no%20text%20visible&width=600&height=400&seq=about-office&orientation=landscape"
                  alt="事務所内観"
                  className="w-full h-64 md:h-96 object-cover object-top rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Company Information */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 md:mb-12">事務所情報</h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="p-5 md:p-8">
                    <table className="w-full">
                      <tbody className="space-y-4">
                        <tr className="border-b border-gray-200">
                          <td className="py-3 md:py-4 font-semibold text-gray-900 w-1/3 text-xs md:text-base">事務所名</td>
                          <td className="py-3 md:py-4 text-gray-700 text-xs md:text-base">おかやま司法書士事務所</td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="py-3 md:py-4 font-semibold text-gray-900 text-xs md:text-base">代表司法書士</td>
                          <td className="py-3 md:py-4 text-gray-700 text-xs md:text-base">岡山 太郎</td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="py-3 md:py-4 font-semibold text-gray-900 text-xs md:text-base">設立</td>
                          <td className="py-3 md:py-4 text-gray-700 text-xs md:text-base">2000年</td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="py-3 md:py-4 font-semibold text-gray-900 text-xs md:text-base">従業員数</td>
                          <td className="py-3 md:py-4 text-gray-700 text-xs md:text-base">5名</td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="py-3 md:py-4 font-semibold text-gray-900 text-xs md:text-base">所在地</td>
                          <td className="py-3 md:py-4 text-gray-700 text-xs md:text-base">
                            〒700-0000<br />
                            岡山市北区○○町1-2-3
                          </td>
                        </tr>
                        <tr className="border-b border-gray-200">
                          <td className="py-3 md:py-4 font-semibold text-gray-900 text-xs md:text-base">連絡先</td>
                          <td className="py-3 md:py-4 text-gray-700 text-xs md:text-base">
                            TEL: 086-012-3456<br />
                            FAX: 086-012-3457
                          </td>
                        </tr>
                        <tr>
                          <td className="py-3 md:py-4 font-semibold text-gray-900 text-xs md:text-base">営業時間</td>
                          <td className="py-3 md:py-4 text-gray-700 text-xs md:text-base">
                            9:00〜18:00<br />
                            定休日：日曜日・祝日
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="p-5 md:p-8 bg-gray-50">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">資格・認可・許可</h3>
                    <ul className="space-y-2 md:space-y-3 text-gray-700 text-xs md:text-base">
                      <li className="flex items-center">
                        <i className="ri-check-line text-blue-600 mr-2 md:mr-3 flex-shrink-0"></i>
                        <span>司法書士（岡山県司法書士会所属）</span>
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-blue-600 mr-2 md:mr-3 flex-shrink-0"></i>
                        <span>簡裁訴訟代理等関係業務認定</span>
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-blue-600 mr-2 md:mr-3 flex-shrink-0"></i>
                        <span>成年後見人候補者名簿登録</span>
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-blue-600 mr-2 md:mr-3 flex-shrink-0"></i>
                        <span>日本司法書士会連合会会員</span>
                      </li>
                    </ul>
                    
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6 mt-6 md:mt-8">加入団体</h3>
                    <ul className="space-y-2 md:space-y-3 text-gray-700 text-xs md:text-base">
                      <li className="flex items-center">
                        <i className="ri-check-line text-blue-600 mr-2 md:mr-3 flex-shrink-0"></i>
                        <span>岡山県司法書士会</span>
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-blue-600 mr-2 md:mr-3 flex-shrink-0"></i>
                        <span>日本司法書士会連合会</span>
                      </li>
                      <li className="flex items-center">
                        <i className="ri-check-line text-blue-600 mr-2 md:mr-3 flex-shrink-0"></i>
                        <span>岡山商工会議所</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div className="bg-blue-50 rounded-lg p-5 md:p-8">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-600 rounded-lg flex items-center justify-center mb-4 md:mb-6">
                  <i className="ri-target-line text-2xl md:text-3xl text-white"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">私たちの使命</h3>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  法的手続きの専門家として、お客様の大切な財産と権利を守り、
                  安心して生活できる社会の実現に貢献することが私たちの使命です。
                  複雑な法的手続きを分かりやすく説明し、お客様に寄り添ったサービスを提供いたします。
                </p>
              </div>
              
              <div className="bg-green-50 rounded-lg p-5 md:p-8">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-green-600 rounded-lg flex items-center justify-center mb-4 md:mb-6">
                  <i className="ri-eye-line text-2xl md:text-3xl text-white"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">私たちのビジョン</h3>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  岡山県内で最も信頼される司法書士事務所として、
                  地域の皆様の法的ニーズに応え続けることを目指しています。
                  常に最新の法的知識を習得し、質の高いサービスを提供してまいります。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Access Map */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-8 md:mb-12">アクセス</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 max-w-6xl mx-auto">
              <div>
                <div className="bg-white rounded-lg shadow-lg p-5 md:p-8">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">交通アクセス</h3>
                  
                  <div className="space-y-4 md:space-y-6">
                    <div className="flex items-start">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                        <i className="ri-train-line text-xl md:text-2xl text-blue-600"></i>
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">電車でお越しの場合</h4>
                        <p className="text-gray-600 text-xs md:text-sm">JR岡山駅東口より徒歩10分<br />岡山電鉄東山線「県庁通り駅」より徒歩3分</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                        <i className="ri-car-line text-xl md:text-2xl text-blue-600"></i>
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">お車でお越しの場合</h4>
                        <p className="text-gray-600 text-xs md:text-sm">山陽自動車道「岡山IC」より約15分<br />専用駐車場3台完備</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3 md:mr-4 flex-shrink-0">
                        <i className="ri-bus-line text-xl md:text-2xl text-blue-600"></i>
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-gray-900 mb-1 text-sm md:text-base">バスでお越しの場合</h4>
                        <p className="text-gray-600 text-xs md:text-sm">岡山駅前バス停より「県庁前行き」乗車<br />「県庁前」バス停下車徒歩2分</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3281.8!2d133.9!3d34.7!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDQyJzAwLjAiTiAxMzPCsDU0JzAwLjAiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="おかやま司法書士事務所の地図"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
